import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Box from '@mui/material/Box'
import MuiIconButton from '@mui/material/IconButton'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import SwiperContainer, { type SwiperElement } from 'components/work-order/details/exceptions/SwiperContainer'

import type { Swiper } from 'swiper'

const EXCEPTION_CARD_WIDTH = 275
const SLIDE_GAP_PX = 16

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  background: theme.palette.background.paper,
  '&.Mui-disabled': {
    background: theme.palette.background.paper,
  },
  '&:hover': {
    background: theme.palette.background.paper,
  },
  borderWidth: 1,
  borderColor: theme.palette.border.lightGrey,
  borderStyle: 'solid',
  aspectRatio: 1,
  width: 28,
  height: 28,
}))

type ExceptionSwiperContainerProps = {
  exceptions?: unknown[]
  children?: ReactNode
}

const ExceptionSwiperContainer = ({ exceptions, children }: ExceptionSwiperContainerProps) => {
  const swiperRef = useRef<SwiperElement | null>(null)
  const theme = useTheme()
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const isCustomXL = useMediaQuery('(min-width: 1536px)')
  const isCustomLarge = useMediaQuery('(min-width: 1200px)')
  const isCustomMedium = useMediaQuery('(min-width: 900px)')
  const isCustomSmall = useMediaQuery('(min-width: 300px)')
  const exceptionsCount = exceptions?.length ?? 0

  const getShowSwiperNavigationArrows = () => {
    if (isCustomXL) return exceptionsCount > 4
    if (isCustomLarge || isCustomMedium) return exceptionsCount > 3
    if (isCustomSmall) return exceptionsCount > 2
    return exceptionsCount > 2
  }

  const showSwiperNavigationArrows = getShowSwiperNavigationArrows()

  const updateNavigationState = useCallback((swiperInstance: Swiper) => {
    setIsEnd(swiperInstance.isEnd)
    setIsBeginning(swiperInstance.isBeginning)
  }, [])

  const handleSwiperPrev = useCallback(() => {
    swiperRef.current?.swiper?.slidePrev(300)
    swiperRef.current?.swiper?.update()
  }, [])

  const handleSwiperNext = useCallback(() => {
    swiperRef.current?.swiper?.slideNext(300)
    swiperRef.current?.swiper?.update()
  }, [])

  useEffect(() => {
    if (Array.isArray(exceptions)) {
      setIsEnd(exceptions.length === 1)
    }
    // Slides added/removed after init (a created or deleted exception) don't re-measure
    // the strip on their own — without this the new card sits past the scrollable extent.
    swiperRef.current?.swiper?.update()
  }, [exceptions])

  return (
    <Box sx={{ position: 'relative' }}>
      <SwiperContainer
        mousewheel
        observer
        className="exceptions-swiper"
        slidesPerView="auto"
        spaceBetween={SLIDE_GAP_PX}
        swiperRef={swiperRef}
        sx={{
          'swiper-container.exceptions-swiper': {
            padding: exceptionsCount === 0 ? 0 : theme.spacing(2, 2, 5),
          },
          'swiper-slide': {
            boxSizing: 'border-box',
            height: 'auto',
            width: `${EXCEPTION_CARD_WIDTH}px`,
          },
          '.swiper-pagination': {
            bottom: theme.spacing(1.5),
            lineHeight: 0,
          },
          '.swiper-pagination-bullet': {
            background: theme.palette.text.disabled,
            opacity: 0.5,
          },
          '.swiper-pagination-bullet-active': {
            background: theme.palette.text.primary,
            opacity: 1,
          },
        }}
        onReachEnd={updateNavigationState}
        onSlideChange={updateNavigationState}
      >
        {children}
      </SwiperContainer>
      {showSwiperNavigationArrows && (
        <>
          <Box
            sx={{
              alignItems: 'center',
              bottom: theme.spacing(1.5),
              display: 'flex',
              left: theme.spacing(2),
              position: 'absolute',
              zIndex: 1,
            }}
          >
            <IconButton disabled={isBeginning} onClick={handleSwiperPrev}>
              <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              bottom: theme.spacing(1.5),
              display: 'flex',
              position: 'absolute',
              right: theme.spacing(2),
              zIndex: 1,
            }}
          >
            <IconButton disabled={isEnd} onClick={handleSwiperNext}>
              <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  )
}

export default ExceptionSwiperContainer
